import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { createElement } from 'react'
import ptBR from './pt-BR.json'
import en from './en.json'

export type Locale = 'pt-BR' | 'en'

type TranslationMap = Record<string, string>

// Build-time brand substitution. Replaces `{brandFullName}` in any string with
// the value of VITE_BRAND_FULL_NAME (defaults to 'TESSET HUB WiFi' to preserve
// existing deploy). Performed once at module load.
const BRAND_FULL_NAME = import.meta.env.VITE_BRAND_FULL_NAME ?? 'TESSET HUB WiFi'
function substituteBrand(map: TranslationMap): TranslationMap {
  const out: TranslationMap = {}
  for (const [key, value] of Object.entries(map)) {
    out[key] = value.replace(/\{brandFullName\}/g, BRAND_FULL_NAME)
  }
  return out
}

const translations: Record<Locale, TranslationMap> = {
  'pt-BR': substituteBrand(ptBR),
  en: substituteBrand(en),
}

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

function detectLocale(): Locale {
  const stored = localStorage.getItem('hub-wifi-locale')
  if (stored === 'en' || stored === 'pt-BR') return stored
  const browser = navigator.language
  if (browser.startsWith('en')) return 'en'
  return 'pt-BR'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale)

  const setLocale = useCallback((newLocale: Locale) => {
    localStorage.setItem('hub-wifi-locale', newLocale)
    setLocaleState(newLocale)
  }, [])

  const t = useCallback(
    (key: string): string => {
      return translations[locale][key] ?? key
    },
    [locale],
  )

  return createElement(I18nContext.Provider, { value: { locale, setLocale, t } }, children)
}

export function useTranslation() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider')
  }
  return context
}
